import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { fmt, fmtInt } from "@/lib/format";
import { toast } from "sonner";
import { Plus, ArrowUpRight, ArrowDownRight, RefreshCw, Building2, Printer } from "lucide-react";
import { printStockMovement } from "@/lib/printStockMovement";
import { localizeError } from "@/lib/errors";

export default function StockMovements() {
  const { t, lang } = useLanguage();
  const { branches, activeBranchId } = useBranch();
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", qty_delta: 0, reason: "", branch_id: activeBranchId || "" });

  const branchLabel = (b) => (lang === "ar" ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar)) || b.branch_id;
  const branchMap = {};
  branches.forEach((b) => { branchMap[b.branch_id] = branchLabel(b); });

  const load = async () => {
    const movUrl = activeBranchId ? `/inventory-movements?limit=500&branch_id=${encodeURIComponent(activeBranchId)}` : "/inventory-movements?limit=500";
    const prodUrl = activeBranchId ? `/products?branch_id=${encodeURIComponent(activeBranchId)}` : "/products";
    const [m, p] = await Promise.all([api.get(movUrl), api.get(prodUrl)]);
    setMovements(m.data); setProducts(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeBranchId]);

  // Load print template once for invoice headers/branding.
  useEffect(() => {
    api.get("/invoice-template")
      .then((r) => setInvoiceTemplate(r.data || null))
      .catch(() => setInvoiceTemplate(null));
  }, []);

  const submit = async () => {
    if (!form.product_id || !parseInt(form.qty_delta) || !form.reason) { toast.error(t("required_field")); return; }
    if (branches.length > 0 && !form.branch_id) { toast.error(lang === "ar" ? "اختر الفرع" : "Select branch"); return; }
    try {
      await api.post("/inventory-movements/adjust", {
        product_id: form.product_id,
        qty_delta: parseInt(form.qty_delta),
        reason: form.reason,
        branch_id: form.branch_id || undefined,
      });
      toast.success(t("saved")); setOpen(false); setForm({ product_id: "", qty_delta: 0, reason: "", branch_id: activeBranchId || "" }); load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  const productName = (id) => {
    const p = products.find(x => x.product_id === id);
    return p ? (lang === "ar" ? p.name_ar : p.name_en) : id;
  };

  const sku = (id) => products.find(x => x.product_id === id)?.sku || "";

  const KindBadge = ({ kind, qty }) => {
    if (kind === "in") return <span className="badge badge-green flex items-center gap-1"><ArrowUpRight size={12}/>{t("stock_in")}</span>;
    if (kind === "out") return <span className="badge badge-rose flex items-center gap-1"><ArrowDownRight size={12}/>{t("stock_out")}</span>;
    return <span className="badge badge-gold flex items-center gap-1"><RefreshCw size={12}/>{t("stock_adjust")}</span>;
  };

  return (
    <div className="space-y-6" data-testid="stock-movements-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-4xl gold-text">{t("nav_stock_movements")}</h1>
        <button onClick={() => { setForm({ product_id: "", qty_delta: 0, reason: "", branch_id: activeBranchId || "" }); setOpen(true); }} data-testid="adjust-stock-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2">
          <Plus size={16}/>{t("adjust_stock")}
        </button>
      </div>

      <div className="card-surface overflow-auto">
        <table className="table-dark">
          <thead><tr>
            <th>{t("date")}</th><th>{lang === "ar" ? "الفرع" : "Branch"}</th><th>{t("name")}</th><th>{t("sku")}</th><th>{t("status")}</th><th>{t("qty_change")}</th><th>{t("reason")}</th><th>{t("actions")}</th>
          </tr></thead>
          <tbody>
            {movements.map(m => (
              <tr key={m.movement_id}>
                <td className="text-[var(--text-muted)] num-display">{(m.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td>
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--gold-tint)] text-[var(--gold)]">
                    <Building2 size={10}/>{branchMap[m.branch_id] || "—"}
                  </span>
                </td>
                <td className="text-[var(--text)]">{productName(m.product_id)}</td>
                <td className="text-[var(--text-muted)]">{sku(m.product_id)}</td>
                <td><KindBadge kind={m.kind}/></td>
                <td className={`font-medium num-display ${m.qty_delta > 0 ? "gold-text" : "rose-text"}`}>{m.qty_delta > 0 ? "+" : ""}{fmtInt(m.qty_delta)}</td>
                <td className="text-[var(--text-muted)]">{m.reason}</td>
                <td>
                  <button
                    onClick={() => printStockMovement(m, products.find(x => x.product_id === m.product_id), invoiceTemplate, branchMap[m.branch_id] || "", lang)}
                    data-testid={`print-movement-${m.movement_id}`}
                    title={lang === "ar" ? "طباعة" : "Print"}
                    className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--gold)]">
                    <Printer size={14}/>
                  </button>
                </td>
              </tr>
            ))}
            {movements.length === 0 && <tr><td colSpan={8} className="text-center text-[var(--text-muted)] py-8">{t("no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{t("adjust_stock")}</h2>
            <div className="space-y-3">
              {branches.length > 0 && (
                <div>
                  <label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Building2 size={12}/>{lang === "ar" ? "الفرع" : "Branch"}</label>
                  <select className="input-dark" value={form.branch_id} onChange={e => setForm({...form, branch_id: e.target.value})} data-testid="adj-branch">
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    {branches.map((b) => (
                      <option key={b.branch_id} value={b.branch_id}>{branchLabel(b)}{b.is_default ? " ★" : ""}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("select_product")}</label>
                <select className="input-dark" value={form.product_id} onChange={e => setForm({...form, product_id: e.target.value})} data-testid="adj-product">
                  <option value="">---</option>
                  {products.map(p => <option key={p.product_id} value={p.product_id}>{lang === "ar" ? p.name_ar : p.name_en} {p.sku ? `(${p.sku})` : ""} · {t("stock")}: {p.stock}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("qty_change")} (+ {t("stock_in")}, − {t("stock_out")})</label>
                <input type="number" className="input-dark num-display" value={form.qty_delta} onChange={e => setForm({...form, qty_delta: e.target.value})} data-testid="adj-qty"/>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("reason")}</label>
                <input className="input-dark" value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} data-testid="adj-reason" placeholder="Damaged / Counted / etc."/>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-[var(--text)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="adj-save-btn" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
